import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios, { createStripeCheckoutSession } from '../../services/api';
import { useAuth } from '../auth/AuthContext';
import {
  FiArrowRight,
  FiAlertCircle,
  FiCheckCircle,
  FiCreditCard,
  FiLoader,
  FiRefreshCw,
  FiShield,
  FiZap,
  FiEdit3,
  FiUsers,
  FiDollarSign,
  FiMapPin,
  FiCheck,
  FiLayers,
  FiExternalLink
} from 'react-icons/fi';
import './PaymentRequired.scss';

const PaymentRequired = () => {
  const { isAuthenticated, refreshSubscriptionInfo } = useAuth();
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [retrying, setRetrying] = useState(false);

  // Collab offer & plans state
  const [offer, setOffer] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const offerId = location.state?.offerId
    || new URLSearchParams(location.search).get('offerId');

  const offerCreatorCount = Number(offer?.quantity || 0);
  const activeSubscriptionExceedsCreatorLimit = Boolean(
    activeSubscription
    && Number(activeSubscription.max_creators_per_collab || 0) > 0
    && offerCreatorCount > Number(activeSubscription.max_creators_per_collab),
  );

  const language = i18n.resolvedLanguage || i18n.language || 'es';
  const dateLocale = language.startsWith('pt') ? 'pt-BR' : language.startsWith('en') ? 'en-US' : 'es-PA';

  // ---- Fetch Subscription, Collab and Plans ----
  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      setRetrying(false);

      // 1. Subscription status
      const subPromise = axios.get('/check-subscription/').catch(err => {
        console.warn('Subscription check error:', err);
        return { data: { has_active_subscription: false } };
      });

      // 2. Collab Offer details (if offerId present)
      const offerPromise = offerId
        ? axios.get(`/collabs/collab/${offerId}/`).catch(err => {
            console.warn('Offer fetch error:', err);
            return null;
          })
        : Promise.resolve(null);

      // 3. Payment Plans catalog
      const plansPromise = axios.get('/plans/').catch(err => {
        console.warn('Plans fetch error:', err);
        return { data: [] };
      });

      const [subRes, offerRes, plansRes] = await Promise.all([subPromise, offerPromise, plansPromise]);

      // Set subscription data
      if (subRes?.data?.has_active_subscription) {
        const sub = subRes.data.active_subscription;
        setActiveSubscription(sub);
      } else {
        setActiveSubscription(null);
      }

      // Set offer data
      if (offerRes?.data) {
        setOffer(offerRes.data);
      }

      // Set plans data
      const allPlans = Array.isArray(plansRes?.data)
        ? plansRes.data
        : (plansRes?.data?.results || []);
      const plansList = allPlans.filter(plan => !plan?.plan_type || plan.plan_type === 'subscription');
      setPlans(plansList);

      // Pre-select recommended plan (Growth) or first available
      if (plansList.length > 0) {
        const defaultMonthly = plansList.filter(p => !p.billing_period || p.billing_period === 'monthly');
        const recommended = defaultMonthly.find(p => p.name.toLowerCase().includes('growth'))
          || defaultMonthly[0]
          || plansList[0];
        if (recommended) {
          setSelectedPlanId(recommended.id);
        }
      }
    } catch (err) {
      console.error('Error al inicializar pasarela de pago:', err);
      setError(t('paymentRequired.errors.check_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, offerId]); // eslint-disable-line

  useEffect(() => {
    if (!plans.length) return;
    const selected = plans.find((plan) => plan.id === selectedPlanId);
    const selectedIsCompatible = selected
      && (Number(selected.max_creators_per_collab || 0) === 0
        || offerCreatorCount <= Number(selected.max_creators_per_collab));
    if (selectedIsCompatible) return;
    const compatible = plans.find((plan) => (
      Number(plan.max_creators_per_collab || 0) === 0
      || offerCreatorCount <= Number(plan.max_creators_per_collab)
    ));
    setSelectedPlanId(compatible?.id || null);
  }, [offerCreatorCount, plans, selectedPlanId]);

  const handleRetry = () => {
    setRetrying(true);
    fetchData();
  };

  // ---- Handle Billing Period Switcher ----
  const handleSelectBillingPeriod = (nextPeriod) => {
    setBillingPeriod(nextPeriod);
    const currentPlan = plans.find(p => p.id === selectedPlanId);
    if (!currentPlan) return;

    const tierKey = currentPlan.name.toLowerCase().includes('boutique')
      ? 'boutique'
      : currentPlan.name.toLowerCase().includes('growth')
      ? 'growth'
      : 'corporate';

    const matching = plans.find(p => {
      const pPeriod = p.billing_period || 'monthly';
      return pPeriod === nextPeriod && p.name.toLowerCase().includes(tierKey);
    });

    if (matching) {
      setSelectedPlanId(matching.id);
    }
  };

  // Filter plans according to active billing period
  const filteredPlans = useMemo(() => {
    if (!Array.isArray(plans)) return [];
    return plans.filter(p => {
      const pPeriod = p.billing_period || 'monthly';
      return pPeriod === billingPeriod;
    });
  }, [plans, billingPeriod]);

  // Price formatting helper
  const getPlanPriceDisplay = (plan, period) => {
    if (!plan) return { monthlyPrice: '$0', periodBilled: '' };
    const numPrice = Number(plan.price) || 0;
    if (period === 'monthly') {
      return {
        monthlyPrice: `$${numPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
        periodBilled: t('paymentRequired.checkout.billed_period')
      };
    } else if (period === 'semiannual') {
      const perMonth = Math.round(numPrice / 6);
      return {
        monthlyPrice: `$${perMonth.toLocaleString('en-US')}`,
        periodBilled: `$${numPrice.toLocaleString('en-US')} / 6 meses`
      };
    } else {
      const perMonth = Math.round(numPrice / 12);
      return {
        monthlyPrice: `$${perMonth.toLocaleString('en-US')}`,
        periodBilled: `$${numPrice.toLocaleString('en-US')} / año`
      };
    }
  };

  // ---- Stripe Checkout ----
  const handleStripeCheckout = async () => {
    if (!selectedPlanId || isProcessingCheckout) return;

    const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
    if (
      offerCreatorCount > 0
      && selectedPlan
      && Number(selectedPlan.max_creators_per_collab || 0) > 0
      && offerCreatorCount > Number(selectedPlan.max_creators_per_collab)
    ) {
      setError(`Este plan permite hasta ${selectedPlan.max_creators_per_collab} creators por collab; tu collab requiere ${offerCreatorCount}.`);
      return;
    }

    try {
      setIsProcessingCheckout(true);
      setError('');

      const response = await createStripeCheckoutSession({
        planId: selectedPlanId,
        offerId: offerId || null,
      });

      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }

      setError('Stripe no devolvió una URL de pago válida.');
    } catch (err) {
      console.error('Error al conectar con Stripe:', err);
      setError(err.response?.data?.error || err.message || t('paymentRequired.errors.check_failed'));
    } finally {
      setIsProcessingCheckout(false);
    }
  };

  // ---- Publish with Active Subscription ----
  const handlePublishWithSubscription = async () => {
    if (!activeSubscription || !offerId || isPublishing) return;

    try {
      setIsPublishing(true);
      setError('');

      await axios.post(`/collabs/collab/${offerId}/publish/`, {});
      await refreshSubscriptionInfo();

      navigate('/business/offers', {
        state: { message: t('paymentRequired.success.published') },
      });
    } catch (err) {
      setError(err.response?.data?.error || t('paymentRequired.errors.publish_failed'));
    } finally {
      setIsPublishing(false);
    }
  };

  const handleContinueToFunding = () => {
    if (offerId) navigate(`/business/collabs/${offerId}/journey?step=funding`);
  };

  // ---- Loading Screen ----
  if (loading) {
    return (
      <div className="payment-required-editorial-container">
        <div className="editorial-loading-card">
          <div className="spinner-golden" />
          <p>{retrying ? t('paymentRequired.loading.retrying') : t('paymentRequired.loading.checking')}</p>
        </div>
      </div>
    );
  }

  const isPaidOffer = Boolean(offer && (offer.compensation_mode === 'paid' || offer.compensation_mode === 'hybrid' || offer.sponsor_this_offer));
  const insufficientCredits = Boolean(!isPaidOffer && activeSubscription &&
    activeSubscription.max_collabs > 0 && activeSubscription.used_collabs >= activeSubscription.max_collabs);
  const isGifting = !isPaidOffer;
  const collabTypeTag = isGifting ? t('paymentRequired.collab_preview.gifting_tag') : t('paymentRequired.collab_preview.paid_tag');

  return (
    <div className="payment-required-editorial-container">
      <div className="payment-required-editorial-shell">
        
        {/* Editorial Page Header */}
        <header className="pr-hero-header">
          <div className="pr-eyebrow-pill">
            <span className="gold-dot" aria-hidden="true" />
            {t('paymentRequired.eyebrow')}
          </div>
          <h1>
            {insufficientCredits
              ? t('paymentRequired.limit.title')
              : t('paymentRequired.title')}
          </h1>
          <p className="pr-hero-subtext">
            {insufficientCredits
              ? t('paymentRequired.limit.description')
              : activeSubscription
              ? t('paymentRequired.active.description')
              : t('paymentRequired.no_plan.description')}
          </p>
        </header>

        {/* Global Error Banner */}
        {error && (
          <div className="pr-alert-banner" role="alert">
            <FiAlertCircle className="alert-icon" />
            <span className="alert-message">{error}</span>
            <button type="button" className="retry-btn" onClick={handleRetry}>
              <FiRefreshCw /> {t('paymentRequired.actions.retry')}
            </button>
          </div>
        )}

        {/* Two-Column Editorial Grid */}
        <div className="pr-split-layout">
          
          {/* LEFT COLUMN: Collab Brief Preview Card or Platform Feature Card */}
          <aside className="pr-column pr-preview-column">
            {offer ? (
              <article className="pr-collab-card">
                <div className="pr-collab-card-top">
                  <span className="pr-kicker">{t('paymentRequired.collab_preview.kicker')}</span>
                  <span className="pr-status-badge">
                    <span className="status-pulse" />
                    {t('paymentRequired.collab_preview.badge_ready')}
                  </span>
                </div>

                {/* Collab Visual Cover */}
                <div className="pr-collab-cover">
                  {offer.image ? (
                    <img src={offer.image} alt={offer.title} className="cover-img" />
                  ) : (
                    <div className="cover-placeholder">
                      <div className="cover-watermark" aria-hidden="true" />
                      <span className="cover-badge">{collabTypeTag}</span>
                    </div>
                  )}
                  {offer.image && (
                    <span className="cover-badge overlay">{collabTypeTag}</span>
                  )}
                </div>

                <div className="pr-collab-body">
                  <h2 className="pr-collab-title">{offer.title || t('paymentRequired.collab_preview.unspecified')}</h2>
                  
                  {offer.description && (
                    <p className="pr-collab-desc">
                      {offer.description.replace(/<[^>]*>?/gm, '').slice(0, 140)}…
                    </p>
                  )}

                  <div className="pr-meta-grid">
                    <div className="meta-item">
                      <span className="meta-label">
                        <FiUsers className="meta-icon" />
                        {t('paymentRequired.collab_preview.target_creators')}
                      </span>
                      <strong className="meta-value">
                        {offer.quantity || 1} {t('paymentRequired.collab_preview.creators_unit')}
                      </strong>
                    </div>

                    <div className="meta-item">
                      <span className="meta-label">
                        <FiDollarSign className="meta-icon" />
                        {t('paymentRequired.collab_preview.compensation')}
                      </span>
                      <strong className="meta-value">
                        {offer.value ? `$${Number(offer.value).toLocaleString('en-US')}` : t('paymentRequired.collab_preview.unspecified')}
                      </strong>
                    </div>

                    <div className="meta-item">
                      <span className="meta-label">
                        <FiMapPin className="meta-icon" />
                        {t('paymentRequired.collab_preview.location')}
                      </span>
                      <strong className="meta-value">
                        {offer.country || 'PA'}
                      </strong>
                    </div>

                    <div className="meta-item">
                      <span className="meta-label">
                        <FiLayers className="meta-icon" />
                        {t('paymentRequired.collab_preview.format')}
                      </span>
                      <strong className="meta-value">
                        {offer.deliverables_mode === 'defined'
                          ? 'Definidos'
                          : t('paymentRequired.collab_preview.open_deliverables')}
                      </strong>
                    </div>
                  </div>
                </div>

                <div className="pr-collab-footer">
                  <Link to={`/business/collabs/${offerId}/edit`} className="edit-draft-link">
                    <FiEdit3 /> {t('paymentRequired.collab_preview.edit_draft')}
                  </Link>
                </div>
              </article>
            ) : (
              /* Fallback Platform Value Card if no offerId */
              <article className="pr-platform-card">
                <div className="platform-icon-box">
                  <FiShield />
                </div>
                <h3>{t('paymentRequired.collab_preview.platform_title')}</h3>
                <p>{t('paymentRequired.collab_preview.platform_desc')}</p>
                <ul className="platform-perks">
                  <li>
                    <FiCheck className="perk-check" />
                    <span>{t('paymentRequired.collab_preview.feature_creators')}</span>
                  </li>
                  <li>
                    <FiCheck className="perk-check" />
                    <span>{t('paymentRequired.collab_preview.feature_deliverables')}</span>
                  </li>
                  <li>
                    <FiCheck className="perk-check" />
                    <span>{t('paymentRequired.collab_preview.feature_analytics')}</span>
                  </li>
                </ul>
              </article>
            )}
          </aside>

          {/* RIGHT COLUMN: Activation, Subscription or Plans Module */}
          <main className="pr-column pr-action-column">

            {/* STATE A: ACTIVE SUBSCRIPTION WITH AVAILABLE QUOTA */}
            {activeSubscription && !insufficientCredits && !activeSubscriptionExceedsCreatorLimit && (
              <section className="pr-action-card pr-active-card">
                <div className="card-header-row">
                  <div>
                    <span className="kicker-gold">{t('paymentRequired.active.kicker')}</span>
                    <h2>{activeSubscription.plan?.name || t('paymentRequired.active.plan_fallback')}</h2>
                  </div>
                  <span className="pr-badge-success">
                    <FiCheckCircle /> {t('paymentRequired.active.badge')}
                  </span>
                </div>

                <div className="active-metrics-grid">
                  <div className="metric-box">
                    <span className="metric-label">{t('paymentRequired.active.available')}</span>
                    <strong className="metric-number">
                      {activeSubscription.max_collabs === 0
                        ? '∞'
                        : Math.max(activeSubscription.max_collabs - activeSubscription.used_collabs, 0)}
                    </strong>
                    <small className="metric-sub">
                      {activeSubscription.max_collabs === 0
                        ? t('paymentRequired.active.unlimited')
                        : t('paymentRequired.active.collabs_remaining')}
                    </small>
                  </div>

                  <div className="metric-box">
                    <span className="metric-label">{t('paymentRequired.active.creator_limit')}</span>
                    <strong className="metric-number">
                      {activeSubscription.max_creators_per_collab === 0
                        ? '∞'
                        : activeSubscription.max_creators_per_collab}
                    </strong>
                    <small className="metric-sub">{t('paymentRequired.active.per_collab')}</small>
                  </div>

                  <div className="metric-box">
                    <span className="metric-label">{t('paymentRequired.active.renewal')}</span>
                    <strong className="metric-number">
                      {activeSubscription.end_date
                        ? new Date(activeSubscription.end_date).toLocaleDateString(dateLocale, { day: '2-digit', month: 'short' })
                        : '—'}
                    </strong>
                    <small className="metric-sub">
                      {activeSubscription.end_date
                        ? t('paymentRequired.active.valid_until')
                        : t('paymentRequired.active.no_expiry')}
                    </small>
                  </div>
                </div>

                <div className="active-protection-note">
                  <FiZap className="zap-gold" />
                  <span>{t(isPaidOffer ? 'paymentRequired.active.paid_note' : 'paymentRequired.active.note')}</span>
                </div>

                <button
                  type="button"
                  className="pr-btn pr-btn-publish"
                  onClick={isPaidOffer ? handleContinueToFunding : handlePublishWithSubscription}
                  disabled={isPublishing}
                >
                  {isPublishing ? (
                    <>
                      <FiLoader className="btn-spinner" />
                      {t('paymentRequired.actions.processing')}
                    </>
                  ) : (
                    <>
                      <FiCheckCircle />
                      {isPaidOffer ? t('paymentRequired.actions.continue_funding') : t('paymentRequired.actions.publish')}
                      <FiArrowRight className="btn-arrow" />
                    </>
                  )}
                </button>
              </section>
            )}

            {activeSubscriptionExceedsCreatorLimit && (
              <section className="pr-action-card pr-limit-card">
                <div className="card-header-row">
                  <div>
                    <span className="kicker-warning">Límite de creators</span>
                    <h2>{activeSubscription?.plan?.name || t('paymentRequired.active.plan_fallback')}</h2>
                  </div>
                  <span className="pr-badge-warning"><FiAlertCircle /> No compatible</span>
                </div>
                <p className="limit-explanation">
                  Este collab requiere {offerCreatorCount} creators, pero tu plan permite hasta {activeSubscription.max_creators_per_collab} por collab.
                  Reduce la cantidad desde el borrador o solicita un upgrade antes de continuar.
                </p>
                {offerId && (
                  <Link to={`/business/collabs/${offerId}/edit`} className="pr-btn pr-btn-upgrade">
                    <FiEdit3 /> Editar collab
                  </Link>
                )}
              </section>
            )}

            {/* STATE B: QUOTA EXHAUSTED */}
            {insufficientCredits && (
              <section className="pr-action-card pr-limit-card">
                <div className="card-header-row">
                  <div>
                    <span className="kicker-warning">{t('paymentRequired.limit.badge')}</span>
                    <h2>{activeSubscription?.plan?.name || t('paymentRequired.active.plan_fallback')}</h2>
                  </div>
                  <span className="pr-badge-warning">
                    <FiAlertCircle /> {t('paymentRequired.limit.exhausted')}
                  </span>
                </div>

                <div className="limit-usage-bar-group">
                  <div className="usage-labels">
                    <span>{t('paymentRequired.limit.usage_label')}</span>
                    <strong>
                      {activeSubscription?.used_collabs || 0} / {activeSubscription?.max_collabs || 0}
                    </strong>
                  </div>
                  <div className="usage-track">
                    <div className="usage-fill" style={{ width: '100%' }} />
                  </div>
                </div>

                <p className="limit-explanation">{t('paymentRequired.limit.body')}</p>

                {/* Plan Chooser embedded for Upgrade */}
                <div className="upgrade-tier-prompt">
                  <Link
                    to={`/business/plans?offerId=${offerId || ''}`}
                    className="pr-btn pr-btn-upgrade"
                  >
                    {t('paymentRequired.actions.view_plans')}
                    <FiArrowRight className="btn-arrow" />
                  </Link>
                </div>
              </section>
            )}

            {/* STATE C: NO ACTIVE SUBSCRIPTION (Integrated Plan Selector & Checkout) */}
            {!activeSubscription && !insufficientCredits && (
              <section className="pr-action-card pr-checkout-card">
                <div className="checkout-card-header">
                  <div>
                    <span className="kicker-gold">{t('paymentRequired.no_plan.kicker')}</span>
                    <h2>{t('paymentRequired.checkout.select_plan')}</h2>
                  </div>
                  <p className="checkout-subtext">{t('paymentRequired.no_plan.body')}</p>
                </div>

                {/* Segmented Pill Selector for Billing Period */}
                <div className="pr-period-selector" role="tablist" aria-label="Períodos de facturación">
                  <button
                    type="button"
                    className={`period-pill ${billingPeriod === 'monthly' ? 'active' : ''}`}
                    onClick={() => handleSelectBillingPeriod('monthly')}
                    role="tab"
                    aria-selected={billingPeriod === 'monthly'}
                  >
                    {t('paymentRequired.checkout.monthly')}
                  </button>
                  <button
                    type="button"
                    className={`period-pill ${billingPeriod === 'semiannual' ? 'active' : ''}`}
                    onClick={() => handleSelectBillingPeriod('semiannual')}
                    role="tab"
                    aria-selected={billingPeriod === 'semiannual'}
                  >
                    {t('paymentRequired.checkout.semiannual')}
                    <span className="save-tag">{t('paymentRequired.checkout.save_5')}</span>
                  </button>
                  <button
                    type="button"
                    className={`period-pill ${billingPeriod === 'annual' ? 'active' : ''}`}
                    onClick={() => handleSelectBillingPeriod('annual')}
                    role="tab"
                    aria-selected={billingPeriod === 'annual'}
                  >
                    {t('paymentRequired.checkout.annual')}
                    <span className="save-tag">{t('paymentRequired.checkout.save_10')}</span>
                  </button>
                </div>

                {/* Plan Cards Grid */}
                <div className="pr-plan-options-grid" role="radiogroup" aria-label="Planes de suscripción FOTH">
                  {filteredPlans.map(plan => {
                    const isSelected = selectedPlanId === plan.id;
                    const isCompatible = Number(plan.max_creators_per_collab || 0) === 0
                      || offerCreatorCount <= Number(plan.max_creators_per_collab);
                    const isGrowth = plan.name.toLowerCase().includes('growth');
                    const isBoutique = plan.name.toLowerCase().includes('boutique');
                    const { monthlyPrice, periodBilled } = getPlanPriceDisplay(plan, billingPeriod);
                    const cleanName = plan.name.replace(/ (6 Meses|Anual)$/i, '');

                    return (
                      <div
                        key={plan.id}
                        className={`pr-plan-tier-card ${isSelected ? 'is-selected' : ''} ${isGrowth ? 'is-recommended' : ''} ${!isCompatible ? 'is-disabled' : ''}`}
                        onClick={() => isCompatible && setSelectedPlanId(plan.id)}
                        role="radio"
                        aria-checked={isSelected}
                        aria-disabled={!isCompatible}
                        tabIndex={isCompatible ? 0 : -1}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && isCompatible) {
                            e.preventDefault();
                            setSelectedPlanId(plan.id);
                          }
                        }}
                      >
                        <div className="tier-header">
                          <span className={`tier-badge ${isGrowth ? 'recommended' : ''}`}>
                            {isGrowth
                              ? t('paymentRequired.checkout.recommended_badge')
                              : isBoutique
                              ? t('paymentRequired.checkout.starter_badge')
                              : t('paymentRequired.checkout.teams_badge')}
                          </span>
                          <span className={`radio-dot ${isSelected ? 'checked' : ''}`} />
                        </div>

                        <h3 className="tier-name">{cleanName}</h3>
                        
                        <div className="tier-pricing">
                          <strong className="rate">{monthlyPrice}</strong>
                          <span className="per-month">{t('paymentRequired.checkout.per_month')}</span>
                        </div>
                        <div className="tier-billed-note">{periodBilled}</div>

                        <div className="tier-capacity">
                          <FiCheck className="capacity-check" />
                          <span>
                            {plan.max_collabs === 0
                              ? 'Collabs ilimitados'
                              : `${plan.max_collabs} collabs activas`}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Direct Checkout CTA & Fee Transparency */}
                <div className="pr-checkout-cta-area">
                  <button
                    type="button"
                    className="pr-btn pr-btn-checkout"
                    onClick={handleStripeCheckout}
                    disabled={isProcessingCheckout || !selectedPlanId || !plans.find((plan) => plan.id === selectedPlanId) || !(
                      Number(plans.find((plan) => plan.id === selectedPlanId)?.max_creators_per_collab || 0) === 0
                      || offerCreatorCount <= Number(plans.find((plan) => plan.id === selectedPlanId)?.max_creators_per_collab)
                    )}
                  >
                    {isProcessingCheckout ? (
                      <>
                        <FiLoader className="btn-spinner" />
                        {t('paymentRequired.checkout.connecting_stripe')}
                      </>
                    ) : (
                      <>
                        <FiCreditCard />
                        {t('paymentRequired.checkout.cta_stripe')}
                        <FiArrowRight className="btn-arrow" />
                      </>
                    )}
                  </button>

                  <div className="pr-ssl-note">
                    <FiShield className="shield-icon" />
                    <span>{t('paymentRequired.checkout.ssl_note')}</span>
                  </div>

                  <div className="pr-catalog-link-row">
                    <Link
                      to={`/business/plans?offerId=${offerId || ''}`}
                      className="catalog-link"
                    >
                      {t('paymentRequired.checkout.explore_catalog')}
                      <FiExternalLink />
                    </Link>
                  </div>
                </div>
              </section>
            )}

          </main>
        </div>

        {/* Global Footer Navigation */}
        <footer className="pr-footer-nav">
          <Link to="/business/offers" className="back-link">
            <FiArrowRight className="back-arrow" />
            {t('paymentRequired.actions.back')}
          </Link>
        </footer>

      </div>
    </div>
  );
};

export default PaymentRequired;
